import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import 'devicon/devicon.min.css';
import './Experience.css';

const sampleExperiences = [
    {
        id: 0,
        title: 'Developer Internship',
        company: 'Arctic Wolf · Co-op',
        period: 'Sep 2025 - Dec 2025',
        description: 'Worked on the Alaska Engineering team in a production cloud and DevOps environment, contributing to infrastructure migration, service reliability, and CI/CD automation.',
        skills: ['Amazon Web Services (AWS)', 'Jenkins', 'Terragrunt', 'pytest', 'Docker', 'Terraform', 'Amazon ECS', 'Amazon DynamoDB', 'Python (Programming Language)', 'Problem Solving', 'Slack', 'Jira', 'Documentation']
    },
    {
        id: 1,
        title: 'Web Developer',
        company: 'SAMSKRITA BHARATI, USA · Co-op',
        period: 'Jan 2025 - Apr 2025',
        description: 'Worked with a fellow Waterloo co-op student to design and build an OCR web service from scratch, integrating React.js, Node.js, and Express.js with AWS deployment and user authentication.',
        skills: ['React.js', 'Node.js', 'Express.js', 'AWS', 'Auth0'],
        projectId: 'samskrita-ocr-project',
        isProject: true
    },
    {
        id: 2,
        title: 'President - STEAM Club',
        company: 'Fletcher\'s Meadow Secondary School',
        period: 'Sep 2023 - Jun 2024',
        description: 'Led a dynamic student organization focusing on STEAM education through interactive workshops and team events.',
        skills: ['Team Leadership', 'Event Coordination', 'Creativity and Innovation']
    },
    {
        id: 3,
        title: 'President - Computer Club',
        company: 'Fletcher\'s Meadow Secondary School',
        period: 'Sep 2023 - Jun 2024',
        description: 'Managed coding workshops and fostered learning through creative, interactive programming sessions.',
        skills: ['Python (Programming Language)', 'Public Speaking', 'Algorithms']
    }
    // Add more experiences as needed
];

const getDeviconClass = tech => {
    switch (tech.toLowerCase().replace(/[\s.]/g,'')) {
        case 'c++': return 'devicon-cplusplus-plain colored';
        case 'python': return 'devicon-python-plain colored';
        case 'reactjs': return 'devicon-react-original colored';
        case 'nodejs': return 'devicon-nodejs-plain colored';
        case 'expressjs': return 'devicon-express-original colored';
        case 'tensorflow': return 'devicon-tensorflow-plain colored';
        case 'keras': return 'devicon-keras-plain colored';
        case 'pytorch': return 'devicon-pytorch-plain colored';
        case 'opencv': return 'devicon-opencv-plain colored';
        case 'mongodb': return 'devicon-mongodb-plain colored';
        case 'aws': return 'devicon-amazonwebservices-plain colored';
        case 'auth0': return 'devicon-auth0-plain colored';
        case 'ocr': return 'devicon-python-plain colored';
        // …add or tweak mappings as needed…
        default: return 'devicon-code-plain colored';
    }
};

const Experience = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const selectedSkill = queryParams.get('skill');
    const [expandedExperience, setExpandedExperience] = useState(null);

    const filteredExperiences = sampleExperiences.filter(exp =>
        selectedSkill ? exp.skills.includes(selectedSkill) : true
    );

    return (
        <div className="experience">
            <h1>{selectedSkill ? `Experience using "${selectedSkill}"` : "Experience"}</h1>
            
            {filteredExperiences.length ? filteredExperiences.map(exp => (
                <article key={exp.id} className="experience-item">
                    <h2>{exp.title}</h2>
                    <h3>{exp.company} | {exp.period}</h3>
                    <p>{exp.description}</p>
                    
                    {exp.isProject && (
                        <>
                            <div className="experience-actions">
                                <button 
                                    className="learn-more-btn" 
                                    onClick={() => setExpandedExperience(exp.expandedExperience === exp.id ? null : exp.id)}
                                >
                                    {expandedExperience === exp.id ? "Show Less" : "Learn More"}
                                </button>
                                <Link to={`/projects/${exp.projectId}`} className="project-link">
                                    View Project <FontAwesomeIcon icon={faArrowRight} />
                                </Link>
                            </div>
                            
                            {expandedExperience === exp.id && (
                                <div className="experience-details">
                                    <div className="detail-block left-image">
                                        <div className="detail-image">
                                            <img src="/src/assets/images/samskrita-1.jpg" alt="Initial Website Development" />
                                        </div>
                                        <div className="detail-text">
                                            <h4>Initial Website Development and API Integration</h4>
                                            <p>As a co-op student at Samskrita Bharati, I collaborated with a University of Waterloo peer to build an internal OCR service from scratch, starting with a localhost prototype and later an internal beta. We designed core components, including a homepage, an OCR processing page, and a history feature to access past OCR results. I implemented the backend using Express.js, integrating Google Cloud Vision API for text extraction from user-uploaded images, as shown in the GoogleOCR class within OCRServices.js. File uploads were handled with Multer, storing images in AWS S3 and metadata in DynamoDB, ensuring a scalable foundation. This early work, developed with minimal guidance, laid the groundwork for iterative improvements and feature expansion.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="detail-block right-image">
                                        <div className="detail-text">
                                            <h4>Cloud Deployment and Infrastructure Setup</h4>
                                            <p>To make the website publicly accessible, we transitioned from localhost to a production environment, experimenting with platforms like GitHub Pages and Render before settling on an AWS EC2 instance. I configured Nginx as a reverse proxy and secured the site with Let's Encrypt SSL certificates, as seen in the server configuration loading /etc/letsencrypt certificates in server.js. This setup ensured secure, efficient traffic handling. After researching cloud options and consulting our manager, who provided a free domain name, we optimized the EC2 instance for performance, laying a robust infrastructure for the OCR service.</p>
                                        </div>
                                        <div className="detail-image">
                                            <img src="/src/assets/images/samskrita-2.jpg" alt="Cloud Deployment" />
                                        </div>
                                    </div>
                                    
                                    <div className="expandable-summary">
                                        <h4>Technical Achievements</h4>
                                        <ul className="technical-summary">
                                            <li>Implemented a backend server using Express with endpoints for OCR file upload, history retrieval, and user preferences management</li>
                                            <li>Integrated OCR services with Google Vision API and Azure Computer Vision for Indic text detection</li>
                                            <li>Utilized AWS S3 for storing uploaded images and DynamoDB for storing OCR results and user preferences</li>
                                            <li>Built a React frontend with Auth0 authentication, dark mode support, and interactive text highlighting</li>
                                            <li>Developed an invitation code system for user access control and engagement generation</li>
                                        </ul>
                                        <Link to={`/projects/${exp.projectId}`} className="view-full-project">
                                            View Full Project Details
                                        </Link>
                                    </div>

                                    <div className="technologies-container">
                                        <p><strong>Technologies:</strong></p>
                                        <div className="tech-icons">
                                            {exp.skills.map(tech => (
                                                <div className="tech-icon" key={tech}>
                                                    <span className="tech-icon-wrapper">
                                                        <i className={getDeviconClass(tech)} />
                                                    </span>
                                                    <span className="tech-name">{tech}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </article>
            )) : (
                <p>No experiences found for this skill.</p>
            )}
        </div>
    );
};

export default Experience;